'use client';

import useScrollToView from '@repo/hooks/dom/useScrollToView';
import {
  type Dispatch,
  type MouseEvent,
  type SetStateAction,
  startTransition,
  useCallback,
  useEffect,
  useRef,
} from 'react';
import { v4 as uuid } from 'uuid';
import type { TableOfContentsHeadingProps } from './types';
import { useHeadingsObserver } from './useHeadingObserver';
import { COMPONENT_ID_DESKTOP } from './utils';

type Args = {
  activeId: string;
  setActiveId: Dispatch<SetStateAction<string>>;
  distanceToSectionWrapper: number;
  headingLevel: number;
  headings: TableOfContentsHeadingProps[];
  fireNavigateToSectionPageAction: (
    overrideProps?: Record<string, string | object>,
    callback?: () => void,
  ) => void;
};

export function useDesktopToc({
  activeId,
  setActiveId,
  distanceToSectionWrapper,
  headingLevel,
  headings,
  fireNavigateToSectionPageAction,
}: Args) {
  const { current: linkRefs } = useRef<Record<string, HTMLAnchorElement>>({});

  const { pauseScrollTracking, reenableScrollTracking } = useHeadingsObserver({
    activeId,
    distanceToSectionWrapper,
    headingLevel,
    headings,
    setActiveId,
    tocId: COMPONENT_ID_DESKTOP,
  });

  const handleClick = useCallback(
    async (event: MouseEvent<HTMLAnchorElement>) => {
      event.preventDefault();

      const href = event.currentTarget.href;
      const headingId = href.substring(href.indexOf('#') + 1);
      const headingContent = event.currentTarget.innerText;

      pauseScrollTracking();

      startTransition(() => {
        setActiveId(headingId);
      });

      fireNavigateToSectionPageAction({
        action_input: `${headings.findIndex(el => el.id === headingId)}`,
        activity_id: uuid(),
        action_location_url: window.location.href,
        additional_attributes: { toc_section: headingContent },
      });

      await useScrollToView(headingId, 'smooth', 30, true);
      reenableScrollTracking();
    },
    [JSON.stringify(headings), fireNavigateToSectionPageAction],
  );

  const handleRef = useCallback((ref: HTMLAnchorElement | null, headingId: string) => {
    if (ref) linkRefs[headingId] = ref;
  }, []);

  useEffect(() => {
    const targetElement = linkRefs[activeId || ''];
    if (targetElement) {
      targetElement.classList.add('selected');
      // Keep the active TOC item visible when the list overflows the nav.
      // The title is sticky, so we use its bottom edge as the top boundary.
      // Only runs if the ToC is larger than the viewport.
      const nav = targetElement.closest('nav');
      if (nav && nav.scrollHeight > nav.clientHeight) {
        const titleEl = nav.firstElementChild as HTMLElement | null;
        const navRect = nav.getBoundingClientRect();
        const titleBottom = titleEl ? titleEl.getBoundingClientRect().bottom : navRect.top;
        const linkRect = targetElement.getBoundingClientRect();
        if (linkRect.top < titleBottom) {
          // Active item is hidden behind the ToC title — scroll up.
          nav.scrollBy({ top: -(titleBottom - linkRect.top), behavior: 'smooth' });
        } else if (linkRect.bottom > navRect.bottom) {
          // Active item is below the visible area — scroll down.
          nav.scrollBy({ top: linkRect.bottom - navRect.bottom, behavior: 'smooth' });
        }
      }
    }
    return () => {
      if (targetElement) {
        targetElement.classList.remove('selected');
      }
    };
  }, [activeId]);

  const progStep = headings.length ? 100 / headings.length : 0;
  const progIndex = headings.findIndex(h => h.id === activeId);
  const progress = progStep * (progIndex + 1);

  return { handleClick, handleRef, progress };
}
